<template>
  <div class="inventory-quantity-picker">
    <v-icon v-if="showArrow">mdi-subdirectory-arrow-right</v-icon>
    <div ref="inventoryQuantityPickerRef" class="inventory-quantity-content">
      <JobInventoryPicker
        ref="inventoryPicker"
        class="inventory-picker"
        :model-value="modelValue.inventorySubTypeId"
        :resource-type-activity-id="resourceTypeActivityId"
        :disable-inventory-sub-types="disableInventorySubTypes"
        :is-disabled="isDisabled"
        :hint="hint"
        :error-messages="inventorySubTypeErrorMessages"
        :required="required"
        data-test="inventory-sub-type-picker"
        @update:modelValue="updateInventorySubTypeId"
      />
      <vs-text-input
        class="inventory-quantity-input"
        :disabled="isDisabled"
        :required="required"
        data-test="inventory-quantity"
        type="number"
        free-form
        item-value="text"
        label="Qty"
        :value="modelValue.quantity"
        :rules="inventoryQuantityRules"
        use-short-messages
        :error-messages="quantityErrorMessages"
        @input="updateQuantity"
      />
    </div>
    <vs-button
      v-if="allowRemoveInventory"
      data-test="remove-inventory-item"
      icon="clear"
      small
      destructive
      type="tertiary"
      :disabled="isDisabled"
      @click="emit('remove-inventory-item')"
    />
  </div>
</template>

<script setup lang="ts">
import { v4 } from 'uuid'
import JobInventoryPicker from './JobInventoryPicker.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import type { InventoryPickerItem } from './types'
import { integerRange } from '@/utils/constants'
import { ref, provide, onMounted, nextTick } from 'vue'
import VsButton from '@/components/vision/VsButton.vue'

interface InventoryAndQuantityPickerProps {
  modelValue?: InventoryPickerItem
  resourceTypeActivityId?: number | null
  disableInventorySubTypes?: number[]
  isDisabled?: boolean
  allowRemoveInventory?: boolean
  showArrow?: boolean
  hint?: string | null
  inventorySubTypeErrorMessages?: string[]
  quantityErrorMessages?: string[]
  required?: boolean
  allowZeroInventory?: boolean
}

const props = withDefaults(defineProps<InventoryAndQuantityPickerProps>(), {
  modelValue: () => ({
    id: null,
    inventorySubTypeId: null,
    quantity: null,
    _uniqueId: v4(),
  }),
  resourceTypeActivityId: null,
  isDisabled: false,
  allowRemoveInventory: false,
  showArrow: true,
  hint: null,
  required: true,
  allowZeroInventory: false,
})

const inventoryQuantityPickerRef = ref<HTMLElement | null>(null)

// Inject the customContainer from parent component to AutocompleteShell
// This is used make the dropdown size has the same width as inventory picker + quantity input all together
provide('customContainer', inventoryQuantityPickerRef)

const emit = defineEmits<{
  (e: 'update:modelValue', value: InventoryPickerItem): void
  (e: 'remove-inventory-item'): void
}>()

const inventoryQuantityRules = [
  (v: string) =>
    props.allowZeroInventory === true
      ? parseFloat(v) < 0 && `Must be 0 or greater`
      : parseFloat(v) <= 0 && `Must be greater than 0`,
  (v: string) => parseFloat(v) > integerRange.max && `Please enter a smaller number`,
  (v: string) => parseFloat(v) < integerRange.min && `Please enter a larger number`,
]

const validateQuantity = (value: string | number): number | null => {
  const parsed = typeof value === 'string' ? parseFloat(value) : value
  return isNaN(parsed) ? null : parsed
}

const updateQuantity = (value: string | number) => {
  const quantity = validateQuantity(value)

  emit('update:modelValue', {
    ...props.modelValue,
    quantity,
  })
}

const updateInventorySubTypeId = (value: number | null | undefined) => {
  emit('update:modelValue', {
    ...props.modelValue,
    inventorySubTypeId: value ?? null,
  })
}

const inventoryPicker = ref<InstanceType<typeof JobInventoryPicker> | null>(null)
onMounted(() => {
  nextTick().then(() => {
    if (props.modelValue?.autofocus) {
      inventoryPicker.value?.focus()
    }
  })
})

defineExpose({
  focus: () => {
    inventoryPicker.value?.focus()
  },
})
</script>

<style scoped>
.inventory-quantity-picker {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.inventory-quantity-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.inventory-picker {
  flex: 1;
}

.inventory-quantity-input {
  width: 6rem;
  align-self: flex-start;
}
</style>
